<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 1200px;">

      <!-- Header -->
      <div class="table-header-filters pa-4 mb-6">
        <v-row dense align="center">
          <v-col cols="12" sm="auto">
            <div class="period-pills">
              <button
                v-for="opt in periodOptions"
                :key="opt.value"
                class="period-pill"
                :class="{ active: periodIndex === opt.value }"
                @click="periodIndex = opt.value"
              >
                <v-icon x-small class="mr-1">{{ opt.icon }}</v-icon>
                {{ opt.label }}
              </button>
            </div>
          </v-col>

          <!-- Custom date range -->
          <v-col v-if="periodIndex === 'custom'" cols="12" sm="auto" class="d-flex align-center" style="gap: 6px;">
            <v-menu v-model="menuFrom" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
                  {{ formatDate(dateFrom) }}
                </button>
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable @input="menuFrom = false; onDateChange()" />
            </v-menu>

            <span class="date-pill-sep">—</span>

            <v-menu v-model="menuTo" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-end</v-icon>
                  {{ formatDate(dateTo) }}
                </button>
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable @input="menuTo = false; onDateChange()" />
            </v-menu>
          </v-col>

          <v-col cols="auto" class="d-flex align-center ml-auto" style="gap: 4px;">
            <v-switch
              v-model="filterPaid"
              dense hide-details
              color="orange darken-1"
              class="mt-0 pt-0 mr-2"
              label="Samo plaćeni"
              @change="refresh"
            />
            <v-btn icon @click="refresh"><v-icon>mdi-refresh</v-icon></v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="{ name: `${targetModifier}.all-reports.reportMenu` }" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="48" />
      </div>

      <template v-else>

        <!-- KPI — Danas -->
        <div class="section-title mb-3">Danas</div>

        <v-row class="mb-2">
          <v-col cols="6" sm="3">
            <v-card outlined rounded="lg" class="pa-4 kpi-card">
              <div class="kpi-label">Prihod danas</div>
              <div class="kpi-value cyan--text text--darken-1">{{ formatEur(todayActiveRevenue) }}</div>
              <div class="kpi-trend mt-1" :class="todayTrend >= 0 ? 'green--text' : 'red--text'">
                <v-icon x-small :color="todayTrend >= 0 ? 'green' : 'red'">
                  {{ todayTrend >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                </v-icon>
                {{ Math.abs(todayTrend).toFixed(1) }}% vs jučer
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined rounded="lg" class="pa-4 kpi-card">
              <div class="kpi-label">Broj računa</div>
              <div class="kpi-value">{{ todayCount }}</div>
              <div class="kpi-trend mt-1 grey--text">jučer: {{ yesterdayCount }}</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined rounded="lg" class="pa-4 kpi-card">
              <div class="kpi-label">Prosjek računa</div>
              <div class="kpi-value">{{ formatEur(todayAvg) }}</div>
              <div class="kpi-trend mt-1" :class="avgTrend >= 0 ? 'green--text' : 'red--text'">
                <v-icon x-small :color="avgTrend >= 0 ? 'green' : 'red'">
                  {{ avgTrend >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                </v-icon>
                {{ Math.abs(avgTrend).toFixed(1) }}% vs jučer
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined rounded="lg" class="pa-4 kpi-card">
              <div class="kpi-label">Jučer prihod</div>
              <div class="kpi-value grey--text">{{ formatEur(yesterdayRevenue) }}</div>
              <div class="kpi-trend mt-1 grey--text">{{ yesterdayCount }} računa</div>
            </v-card>
          </v-col>
        </v-row>

        <div class="breakdown-row mb-2">
          <button
            v-for="item in todayBreakdownItems"
            :key="item.key"
            class="breakdown-chip"
            :class="{ 'breakdown-chip--excluded': excludedKeys.includes(item.key) }"
            @click="toggleExclude(item.key)"
          >
            <v-icon small :color="excludedKeys.includes(item.key) ? '#CBD5E1' : item.color">{{ item.icon }}</v-icon>
            <div class="breakdown-chip__body">
              <span class="breakdown-chip__label">{{ item.label }}</span>
              <span class="breakdown-chip__amount">{{ formatEur(item.amountCents / 100) }}</span>
            </div>
          </button>
        </div>

        <div class="breakdown-hint mb-6">
          <v-icon x-small color="#9CA3AF" class="mr-1">mdi-gesture-tap</v-icon>
          <span>Kliknite na vrstu transakcije za uključivanje/isključivanje iz kalkulacije</span>
        </div>

        <!-- Period section -->
        <div class="section-title mb-3">{{ periodLabel }}</div>
        <v-row>
          <!-- Payment breakdown table -->
          <v-col cols="12" md="7">
            <v-card outlined rounded="lg" class="pa-6" style="position: relative;">
              <div v-if="periodLoading" class="period-loading-overlay">
                <v-progress-circular indeterminate color="primary" size="32" />
              </div>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Način plaćanja</th>
                    <th class="text-right">Broj</th>
                    <th class="text-right">Iznos</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in paymentRows"
                    :key="row.key"
                    class="period-row"
                    :class="{ 'period-row--excluded': excludedPeriodKeys.includes(row.key) }"
                    @click="togglePeriodExclude(row.key)"
                  >
                    <td>
                      <v-icon small class="mr-2" :color="excludedPeriodKeys.includes(row.key) ? '#CBD5E1' : row.color">{{ row.icon }}</v-icon>
                      {{ row.label }}
                    </td>
                    <td class="text-right grey--text">{{ row.count }}</td>
                    <td class="text-right font-weight-medium">{{ formatEur(row.amountEur) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr style="background: #F8FAFC; border-top: 2px solid #E0E6ED;">
                    <td><strong>Ukupno</strong></td>
                    <td class="text-right"><strong>{{ periodTotalCount }}</strong></td>
                    <td class="text-right"><strong>{{ formatEur(periodTotalEur) }}</strong></td>
                  </tr>
                </tfoot>
              </v-simple-table>
              <div class="breakdown-hint mt-3">
                <v-icon x-small color="#9CA3AF" class="mr-1">mdi-gesture-tap</v-icon>
                <span>Kliknite na redak za uključivanje/isključivanje iz kalkulacije</span>
              </div>
            </v-card>
          </v-col>

          <!-- Donut chart -->
          <v-col cols="12" md="5">
            <v-card outlined rounded="lg" class="pa-6" style="position: relative;">
              <div v-if="periodLoading" class="period-loading-overlay">
                <v-progress-circular indeterminate color="primary" size="32" />
              </div>
              <apexchart
                v-if="donutSeries.some(v => v > 0)"
                type="donut"
                height="300"
                :options="donutOptions"
                :series="donutSeries"
              />
              <div v-else class="text-center grey--text py-8">Nema podataka</div>
            </v-card>
          </v-col>
        </v-row>

      </template>
    </div>
  </v-container>
</template>

<script>
import { getFirestore, collection, query, where, getAggregateFromServer, sum, count } from 'firebase/firestore'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import VueApexCharts from 'vue-apexcharts'
import Vue from 'vue'

Vue.use(VueApexCharts)
Vue.component('apexchart', VueApexCharts)

const PAYMENT_METHODS = [
  { key: 'NOVČANICE', label: 'Novčanice', icon: 'mdi-cash', color: '#4CAF50' },
  { key: 'KARTICA', label: 'Kartica', icon: 'mdi-credit-card', color: '#2196F3' },
  { key: 'TRANSAKCIJSKI RAČUN', label: 'Transakcijski', icon: 'mdi-bank', color: '#FF9800' },
  { key: 'OSTALO', label: 'Ostalo', icon: 'mdi-dots-horizontal', color: '#9E9E9E' }
]

const TIP_METHODS = [
  { key: 'NOVČANICE', label: 'Napojnice gotovina', icon: 'mdi-hand-coin-outline', color: '#26A69A' },
  { key: 'KARTICA', label: 'Napojnice kartica', icon: 'mdi-hand-coin', color: '#26C6DA' }
]

const today = new Date().toISOString().substr(0, 10)

export default {
  name: 'StatistikeProhoda',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    return {
      margin: '0px',
      targetModifier: '',
      loading: false,
      periodLoading: false,
      filterPaid: false,
      periodIndex: 'month',
      menuFrom: false,
      menuTo: false,
      dateFrom: today,
      dateTo: today,
      todayTotalCents: 0,
      todayCount: 0,
      yesterdayTotalCents: 0,
      yesterdayCount: 0,
      todayMethodCents: {},
      todayTipsCents: {},
      excludedKeys: [],
      excludedPeriodKeys: [],
      periodData: {},
      tipsData: {},
      periodOptions: [
        { value: 'week', label: 'Tjedan', icon: 'mdi-calendar-week' },
        { value: 'month', label: 'Mjesec', icon: 'mdi-calendar-month' },
        { value: 'quarter', label: 'Kvartal', icon: 'mdi-calendar-range' },
        { value: 'year', label: 'Godina', icon: 'mdi-calendar-blank' },
        { value: 'custom', label: 'Prilagođeno', icon: 'mdi-calendar-edit' }
      ]
    }
  },

  computed: {
    todayRevenue () { return this.todayTotalCents / 100 },
    yesterdayRevenue () { return this.yesterdayTotalCents / 100 },
    yesterdayAvg () { return this.yesterdayCount > 0 ? this.yesterdayRevenue / this.yesterdayCount : 0 },

    todayBreakdownItems () {
      const items = PAYMENT_METHODS.map(m => ({
        key: m.key,
        label: m.label,
        icon: m.icon,
        color: m.color,
        amountCents: this.todayMethodCents[m.key] || 0
      }))
      TIP_METHODS.forEach(m => {
        items.push({
          key: 'TIP_' + m.key,
          label: m.label,
          icon: m.icon,
          color: m.color,
          amountCents: this.todayTipsCents[m.key] || 0
        })
      })
      return items
    },

    todayActiveRevenue () {
      return this.todayBreakdownItems
        .filter(i => !this.excludedKeys.includes(i.key))
        .reduce((s, i) => s + i.amountCents, 0) / 100
    },

    todayAvg () { return this.todayCount > 0 ? this.todayActiveRevenue / this.todayCount : 0 },
    todayTrend () {
      if (!this.yesterdayRevenue) return 0
      return (this.todayActiveRevenue - this.yesterdayRevenue) / this.yesterdayRevenue * 100
    },
    avgTrend () {
      if (!this.yesterdayAvg) return 0
      return (this.todayAvg - this.yesterdayAvg) / this.yesterdayAvg * 100
    },

    periodRange () {
      if (this.periodIndex === 'custom') {
        return {
          start: Math.floor(new Date(this.dateFrom).getTime() / 1000),
          end: Math.floor(new Date(this.dateTo).getTime() / 1000) + 86399
        }
      }
      const now = new Date()
      let start
      if (this.periodIndex === 'week') {
        const day = now.getDay() || 7
        start = new Date(now)
        start.setDate(now.getDate() - day + 1)
        start.setHours(0, 0, 0, 0)
      } else if (this.periodIndex === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1)
      } else if (this.periodIndex === 'quarter') {
        const q = Math.floor(now.getMonth() / 3)
        start = new Date(now.getFullYear(), q * 3, 1)
      } else {
        start = new Date(now.getFullYear(), 0, 1)
      }
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      return {
        start: Math.floor(start.getTime() / 1000),
        end: Math.floor(end.getTime() / 1000)
      }
    },

    periodLabel () {
      if (this.periodIndex === 'custom') return `${this.formatDate(this.dateFrom)} — ${this.formatDate(this.dateTo)}`
      const labels = { week: 'Ovaj tjedan', month: 'Ovaj mjesec', quarter: 'Ovaj kvartal', year: 'Ova godina' }
      return labels[this.periodIndex] || 'Period'
    },

    paymentRows () {
      const rows = PAYMENT_METHODS.map(m => ({
        key: m.key,
        label: m.label,
        icon: m.icon,
        color: m.color,
        count: this.periodData[m.key]?.countVal || 0,
        amountEur: (this.periodData[m.key]?.totalCents || 0) / 100
      }))
      TIP_METHODS.forEach(m => {
        const d = this.tipsData[m.key]
        if (d && (d.totalCents > 0 || d.countVal > 0)) {
          rows.push({ key: 'TIP_' + m.key, label: m.label, icon: m.icon, color: m.color, count: d.countVal, amountEur: d.totalCents / 100 })
        }
      })
      return rows
    },

    activePaymentRows () {
      return this.paymentRows.filter(r => !this.excludedPeriodKeys.includes(r.key))
    },

    periodTotalCount () {
      return this.activePaymentRows.reduce((s, r) => s + r.count, 0)
    },
    periodTotalEur () {
      return this.activePaymentRows.reduce((s, r) => s + r.amountEur, 0)
    },

    donutSeries () {
      return this.activePaymentRows.map(r => parseFloat(r.amountEur.toFixed(2)))
    },
    donutOptions () {
      return {
        chart: { type: 'donut', fontFamily: 'Raleway, sans-serif' },
        labels: this.activePaymentRows.map(r => r.label),
        colors: this.activePaymentRows.map(r => r.color),
        legend: { position: 'bottom' },
        dataLabels: { enabled: true, formatter: val => val.toFixed(1) + '%' },
        tooltip: { y: { formatter: v => this.formatEur(v) } }
      }
    }
  },

  watch: {
    periodIndex (val) {
      if (val === 'custom') {
        this.$nextTick(() => { this.menuFrom = true })
      } else {
        this.loadPeriod()
      }
    }
  },

  async created () {
    this.margin = state.getMargin()
    const sideNav = this.$route.meta?.sideNav
    if (sideNav === 'Company') this.targetModifier = 'company'
    else if (sideNav === 'PointOfSale') this.targetModifier = 'pointOfSale'
    else if (sideNav === 'CashRegister') this.targetModifier = 'cashRegister'
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    await this.refresh()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    formatEur (v) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
    },

    formatDate (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      return `${d}.${m}.${y}`
    },

    onDateChange () {
      if (this.dateFrom && this.dateTo) this.loadPeriod()
    },

    async refresh () {
      this.loading = true
      try {
        await Promise.all([this.fetchDailyKpi(), this.fetchPeriodData()])
      } finally {
        this.loading = false
      }
    },

    async loadPeriod () {
      this.periodLoading = true
      try {
        await this.fetchPeriodData()
      } finally {
        this.periodLoading = false
      }
    },

    toggleExclude (key) {
      const idx = this.excludedKeys.indexOf(key)
      if (idx === -1) this.excludedKeys.push(key)
      else this.excludedKeys.splice(idx, 1)
    },

    togglePeriodExclude (key) {
      const idx = this.excludedPeriodKeys.indexOf(key)
      if (idx === -1) this.excludedPeriodKeys.push(key)
      else this.excludedPeriodKeys.splice(idx, 1)
    },

    async fetchDailyKpi () {
      const companyId = state.getCurrentCompany().id
      const db = getFirestore()
      const col = collection(db, `receipts/${companyId}/receipts`)
      const now = new Date()
      const nowEpoch = Math.floor(now.getTime() / 1000)
      const todayStart = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000)
      const yesterdayStart = todayStart - 86400
      const sf = this.filterPaid ? [where('payment.current_status', 'in', ['PAID', 'REVERSED'])] : []

      const [todaySnap, yesterdaySnap, ...methodSnaps] = await Promise.all([
        getAggregateFromServer(
          query(col, where('issue_date_time', '>=', todayStart), where('issue_date_time', '<=', nowEpoch), ...sf),
          { total: sum('total'), cnt: count() }
        ),
        getAggregateFromServer(
          query(col, where('issue_date_time', '>=', yesterdayStart), where('issue_date_time', '<', todayStart), ...sf),
          { total: sum('total'), cnt: count() }
        ),
        ...PAYMENT_METHODS.map(m =>
          getAggregateFromServer(
            query(col, where('issue_date_time', '>=', todayStart), where('issue_date_time', '<=', nowEpoch), where('payment_method', '==', m.key), ...sf),
            { total: sum('total') }
          )
        )
      ])

      const todayMethodCents = {}
      PAYMENT_METHODS.forEach((m, i) => {
        todayMethodCents[m.key] = methodSnaps[i].data().total || 0
      })
      this.todayMethodCents = todayMethodCents

      let todayCashTips = 0
      let yesterdayCashTips = 0
      const todayTipsCents = { NOVČANICE: 0, KARTICA: 0 }
      try {
        const tipsCol = collection(db, 'tips')
        const [todayTipsAll, todayTipsCard, yesterdayTipsAll, yesterdayTipsCard] = await Promise.all([
          getAggregateFromServer(
            query(tipsCol, where('company_id', '==', companyId), where('created', '>=', todayStart), where('created', '<=', nowEpoch)),
            { total: sum('tip_amount') }
          ),
          getAggregateFromServer(
            query(tipsCol, where('company_id', '==', companyId), where('created', '>=', todayStart), where('created', '<=', nowEpoch), where('tip_payment_method', '==', 'KARTICA')),
            { total: sum('tip_amount') }
          ),
          getAggregateFromServer(
            query(tipsCol, where('company_id', '==', companyId), where('created', '>=', yesterdayStart), where('created', '<', todayStart)),
            { total: sum('tip_amount') }
          ),
          getAggregateFromServer(
            query(tipsCol, where('company_id', '==', companyId), where('created', '>=', yesterdayStart), where('created', '<', todayStart), where('tip_payment_method', '==', 'KARTICA')),
            { total: sum('tip_amount') }
          )
        ])
        const allToday = todayTipsAll.data().total || 0
        const cardToday = todayTipsCard.data().total || 0
        todayCashTips = allToday - cardToday
        todayTipsCents.NOVČANICE = allToday - cardToday
        todayTipsCents.KARTICA = cardToday
        yesterdayCashTips = (yesterdayTipsAll.data().total || 0) - (yesterdayTipsCard.data().total || 0)
      } catch (e) {
        console.error('Tips KPI fetch failed (index needed?):', e)
      }
      this.todayTipsCents = todayTipsCents

      this.todayTotalCents = (todaySnap.data().total || 0) + todayCashTips
      this.todayCount = todaySnap.data().cnt || 0
      this.yesterdayTotalCents = (yesterdaySnap.data().total || 0) + yesterdayCashTips
      this.yesterdayCount = yesterdaySnap.data().cnt || 0
    },

    async fetchPeriodData () {
      const companyId = state.getCurrentCompany().id
      const db = getFirestore()
      const col = collection(db, `receipts/${companyId}/receipts`)
      const { start, end } = this.periodRange
      const sf = this.filterPaid ? [where('payment.current_status', 'in', ['PAID', 'REVERSED'])] : []

      const base = query(col, where('issue_date_time', '>=', start), where('issue_date_time', '<', end), ...sf)

      const results = await Promise.all(
        PAYMENT_METHODS.map(m =>
          getAggregateFromServer(
            query(base, where('payment_method', '==', m.key)),
            { total: sum('total'), cnt: count() }
          )
        )
      )

      const periodData = {}
      PAYMENT_METHODS.forEach((m, i) => {
        periodData[m.key] = {
          totalCents: results[i].data().total || 0,
          countVal: results[i].data().cnt || 0
        }
      })
      this.periodData = periodData

      try {
        const tipsCol = collection(db, 'tips')
        const tipsBase = query(
          tipsCol,
          where('company_id', '==', companyId),
          where('created', '>=', start),
          where('created', '<', end)
        )
        const [tipsAll, tipsCard] = await Promise.all([
          getAggregateFromServer(tipsBase, { total: sum('tip_amount'), cnt: count() }),
          getAggregateFromServer(query(tipsBase, where('tip_payment_method', '==', 'KARTICA')), { total: sum('tip_amount'), cnt: count() })
        ])
        const totalCents = tipsAll.data().total || 0
        const totalCount = tipsAll.data().cnt || 0
        const kartCents = tipsCard.data().total || 0
        const kartCount = tipsCard.data().cnt || 0
        this.tipsData = {
          KARTICA: { totalCents: kartCents, countVal: kartCount },
          NOVČANICE: { totalCents: totalCents - kartCents, countVal: totalCount - kartCount }
        }
      } catch (e) {
        console.error('Tips fetch failed (index needed?):', e)
        this.tipsData = {}
      }
    }
  }
}
</script>

<style scoped>
.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.period-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.period-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1.5px solid #E0E6ED;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  outline: none;
}

.period-pill:hover {
  border-color: #00BCD4;
  color: #00838F;
  background: rgba(0, 188, 212, 0.05);
}

.period-pill.active {
  border-color: #00BCD4;
  background: rgba(0, 188, 212, 0.12);
  color: #00838F;
  font-weight: 600;
}

.date-pill {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
}

.date-pill-sep {
  font-size: 13px;
  color: #9CA3AF;
  font-weight: 500;
}

.period-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  z-index: 5;
}

.section-title { font-size: 16px; font-weight: 700; color: #2d3748; }
.kpi-card { height: 100%; }
.kpi-label { font-size: 12px; color: #6B7280; margin-bottom: 6px; }
.kpi-value { font-size: 20px; font-weight: 700; line-height: 1.2; }
.kpi-trend { font-size: 11px; }

.breakdown-hint {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #6B7280;
  font-style: italic;
}

.breakdown-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.breakdown-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1.5px solid #E0E6ED;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  font-family: inherit;
  text-align: left;
}

.breakdown-chip:hover {
  border-color: #B0BEC5;
  background: #F8FAFC;
}

.breakdown-chip--excluded {
  background: #F8FAFC;
  border-color: #E9ECEF;
  opacity: 0.55;
}

.breakdown-chip__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.breakdown-chip__label {
  font-size: 11px;
  font-weight: 500;
  color: #6B7280;
  line-height: 1;
}

.breakdown-chip--excluded .breakdown-chip__label {
  text-decoration: line-through;
  color: #B0BEC5;
}

.breakdown-chip__amount {
  font-size: 14px;
  font-weight: 700;
  color: #1F2937;
  line-height: 1.2;
}

.breakdown-chip--excluded .breakdown-chip__amount {
  color: #B0BEC5;
}

.period-row {
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.period-row:hover {
  background: #F8FAFC;
}

.period-row--excluded {
  opacity: 0.4;
}

.period-row--excluded td {
  text-decoration: line-through;
  color: #B0BEC5;
}
</style>
